/* STEP 7e-1 (owner "Step 7e Methodology Card V1", 30-Oct-2026 15:10 KSA) — EN + AR.
   M1  the help panel "How the Methodology tab works" (s7e1.help): THE ONE PLACE of the panel's text. The panel
       (components/methodology/MethodologyHelpPanel.jsx) shows the sections in the order of its HELP_SECTIONS list; each section
       here is { title, paras: [...] } and / or { items: [...] } (a numbered list when the section sets "numbered": true).
       STEP 7e-2 (S4) added the sections "settings", "worksheets", "deviations" and "report" and updated "versions" and "who": the
       panel describes exactly what 7e-2 built (firm settings, the 105(a) / Sampling checks, deviations, the firm report).
   M3  the "Change methodology version" dialog of the header badge (s7e1.change) + the two refusals (errors.*).
   M4  the page wording under the title (s7e1.page.wording).
   M5  the Arabic of the Founder Edit-tenant form (s7e1.tenant), of the AI Usage card's fallback texts (s7e1.aiu — the English
       text is unchanged) and the Recycle Bin label of the sample item results (risk.bin.type).
   Loaded from lib/i18n/ by the generated index (STEP 2 · B2). Standards are named without paragraph numbers (owner rule). */
export const I18N_S7E1 = {
  en: {
    s7e1: {
      help: {
        button: "How the Methodology tab works",
        title: "How the Methodology tab works",
        close: "Close",
        sections: {
          what: {
            title: "What this tab is",
            paras: [
              "This tab holds your firm's own audit manual (your methodology) inside AZEX.",
              "AZEX's audit steps already follow the ISAs. Your manual records the choices your firm makes within them — for example the percentage of overall materiality you use for performance materiality. It does not replace the ISAs.",
            ],
          },
          steps: {
            title: "The four steps",
            numbered: true,
            items: [
              "Upload your manual as a PDF (\"Add methodology\").",
              "AZEX lists every section of the manual with its page — for example \"4.5.2 · Performance materiality · 75 % of overall materiality · p. 18\".",
              "AZEX writes a short summary under 8 headings: materiality, sampling, risk assessment, journal entry testing criteria, analytical procedures, external confirmations, review and sign-off levels, and documentation, assembly and archiving. A heading your manual does not cover says \"Not covered in this methodology\".",
              "A manager approves it, and it becomes the current version (V1, V2 …). A draft can also be rejected, with a reason.",
            ],
            paras: [
              "The section list and the summary are AI drafts: check them against the PDF before you approve.",
            ],
          },
          versions: {
            title: "Versions",
            paras: [
              "A new engagement is stamped with the version that is current on the day it is created, and it keeps that version.",
              "Example: V1 is approved in January, so the engagements created in February use V1. You approve V2 in June: the engagements created after that use V2. February's engagements stay on V1 unless a manager changes them, with a reason (the header badge → \"Change methodology version\").",
              "Every new engagement — created from scratch, by roll-forward or by copy — is stamped with the version that is current when it is created. An existing engagement keeps its version until a manager changes it, with a reason.",
              "The header badge \"Methodology Vn\" shows the version of the engagement that is open.",
              "When a new version is approved, the previous one becomes \"Superseded\". Superseded versions stay in the list, with their PDF.",
              "\"Change methodology version\" is refused on a signed-off (locked) engagement: unlock it first.",
              "A version cannot be deleted while it is the current approved version or while any engagement uses it (the message says how many engagements use it).",
              "When a manager changes an engagement's version, the new version's firm settings apply from the next save. Deviations already recorded stay with the version they were recorded under; one closes when its value comes back inside the range it was recorded against.",
            ],
          },
          // STEP 7e-2 · S4: firm settings, where they are used, deviations, the firm report
          settings: {
            title: "Firm settings (one set per version)",
            items: [
              "Each version has its own firm settings (the \"Firm settings\" button on its row): the materiality % range (min–max) for each benchmark — profit before tax, revenue, total assets, equity, expenses and another benchmark; performance materiality % and clearly trivial % of overall materiality (min–max); the minimum sampling confidence level %; the default expected misstatement as % of tolerable misstatement; the significant-risk criteria and the review levels your firm requires (these two are guidance for the team only — they do not change the sign-off or lock rules).",
              "\"Suggest from the methodology\" reads the rules and the summary AZEX listed and suggests a value for each setting with its section and page — for example \"Performance materiality % of overall materiality · 60–75 % · §4.5.2 · p. 18\". The value is read from the rule text; for a setting the rule text does not give, the AI is asked and must point to one of the listed lines. A setting your manual does not state says \"Not found in this methodology\".",
              "A manager confirms each setting before it is used — as suggested, or edited (the suggested value stays next to the edit). A setting that is not confirmed is not used anywhere.",
              "When a version is superseded, its settings are frozen: they stay readable as history and still apply to the engagements that use that version.",
            ],
          },
          worksheets: {
            title: "Where the settings are used",
            items: [
              "Materiality (105(a)): the firm range is shown next to the benchmark %, the performance materiality % and the clearly trivial %. AZEX does not fill these fields in for you. Saving a % outside the range asks for a reason; without one the save is refused. Example: the firm range for performance materiality is 60–75 %; you enter 80 % → AZEX asks for the reason → with the reason the figure is saved and a deviation is recorded.",
              "Sampling: the confidence level starts at the firm minimum; a level below it needs a reason and records a deviation. The expected misstatement starts at the firm default % of the tolerable misstatement; you can change it.",
              "The settings used are those of the version the engagement uses. An engagement with no version of its own, a version with no confirmed settings, or a firm with no approved methodology works as before, with no checks.",
            ],
          },
          deviations: {
            title: "Deviations",
            items: [
              "Each saved figure outside a firm setting is one deviation: the engagement, the area, the setting, the firm's value or range, the engagement's value, the reason, who saved it and when, and the methodology version. Saving the same field again updates that deviation; bringing the value back inside the range closes it.",
              "A new deviation sends a notification to the engagement partner and, when one is appointed, to the EQ reviewer.",
              "The Methodology deviations panel on the File assembly page shows the engagement's deviations to the team, the reviewer and the EQ reviewer. Only the engagement partner acknowledges each one, with an optional note; the acknowledgement is recorded in the Audit Trail. A new value or reason on an open deviation needs the acknowledgement again.",
              "While an open deviation is not acknowledged, the Partner sign-off and the lock are refused with this message (example with one deviation):",
              "\"The Partner sign-off and the lock are refused: the engagement partner has not acknowledged 1 methodology deviation(s): Materiality: Performance materiality % 80 % (firm range 60–75 %). The engagement partner acknowledges each one under Methodology deviations on the File assembly page.\"",
            ],
          },
          report: {
            title: "Firm deviation report",
            paras: [
              "On this page, managers see every deviation across the firm's engagements with its status (open — not acknowledged, open — acknowledged, closed). Filter by engagement, area and status, and export the list to Excel (XLSX).",
            ],
          },
          assistant: {
            title: "Where else it is used",
            paras: [
              "The AI Assistant answers from your manual's rules and cites the section and the page — for example [Methodology V1 §4.5.2, p.18]. It reads the version of the engagement that is open, not simply the newest one.",
            ],
          },
          teamJob: {
            title: "What stays the team's job",
            items: [
              "Firm procedures that AZEX has no worksheet for are documented in your own form or as an attachment.",
              "Topics beyond the 8 headings (for example going concern, group audits, related parties, accounting estimates) are quoted by the Assistant from your manual; AZEX does not turn them into steps or checks.",
              "AZEX does not judge whether your manual complies with the ISAs — that is your firm's professional judgment.",
            ],
          },
          who: {
            title: "Who can do what",
            items: [
              "Upload, approve, reject or delete a version, re-run the extraction (\"Retry\") or the AI summary, run \"Suggest from the methodology\", confirm or change a version's firm settings, change an engagement's version and open the firm deviation report: the Firm Admin, a Partner (including a Partner with the title Director), a Senior Manager, an Audit Manager or a Manager.",
              "An Assistant Manager is not one of them. Everyone else in the firm can open this tab, read the section list, the summary and the firm settings, and view or download the PDF.",
              "Acknowledging a deviation: the engagement partner only.",
              "Recorded in the Audit Trail: uploading, approving, rejecting and deleting a version, re-running the extraction or the AI summary, running the suggestions, confirming or clearing a setting, changing an engagement's version, each deviation (recorded, updated, closed) and each acknowledgement.",
            ],
          },
        },
      },
      page: {
        wording: "AZEX's built-in audit procedures follow the ISAs. Your firm's methodology adds your firm's own choices within them; it does not replace the ISAs.",
      },
      change: {
        menuOpen: "Open the Methodology page",
        menuChange: "Change methodology version…",
        title: "Change methodology version",
        uses: "This engagement uses: V{n}",
        usesFallback: "This engagement has no version of its own; it uses the current approved version V{n}.",
        pick: "New version",
        pickPlaceholder: "Pick a version",
        only: "Only approved or superseded versions can be chosen.",
        none: "There is no approved or superseded version to choose.",
        current: "(this engagement)",
        reason: "Reason (required)",
        reasonPh: "Why this engagement moves to another version",
        reasonRequired: "Enter the reason for changing the methodology version.",
        audit: "The change is recorded in the Audit Trail with the old and the new version and your reason.",
        save: "Change version",
        cancel: "Cancel",
        done: "Methodology version changed: V{old} → V{new}",
      },
      tenant: {
        editTitle: "Edit tenant",
        firmName: "Firm name",
        plan: "Plan",
        pickPlan: "Pick a plan",
        removePlan: "— Remove plan (the firm keeps its current limits) —",
        planItem: "{name} · {users} users · {engs} engs",
        planHint: "Choosing a plan copies its user & engagement caps (a plan with no engagement cap = unlimited engagements). Removing the plan keeps the firm's current caps. Existing users/engagements are never removed if they exceed a cap.",
        cancel: "Cancel",
        save: "Save",
      },
      aiu: {
        headRuntime: "Your own key failed on a recent AI call — served by the platform key instead.",
        headSetup: "Last call used the platform key, not your own key.",
        reasonLbl: "Reason:",
        whenLbl: "When:",
        reason: {
          no_key_stored: "no key stored",
          decryption_failed: "decryption failed",
          runtime_401: "provider rejected the key (401)",
          runtime_403: "provider forbade the request (403)",
          runtime_429: "provider rate-limited (429)",
          runtime_5xx: "provider server error",
          model_retired: "provider retired the saved model (404)",
          runtime_error: "provider error",
          unknown: "unknown",
        },
        fix: {
          decryption_failed: "Fix: re-paste your key below — the stored ciphertext is unreadable.",
          no_key_stored: "Fix: paste your provider key to activate BYOK routing.",
          runtime_401: "Fix: your provider says the key is invalid or revoked — re-paste a current key.",
          runtime_403: "Fix: the key authenticated but lacks permission — check provider console.",
          runtime_429: "No action needed — rate-limit is temporary. Your provider quota should recover.",
          runtime_5xx: "No action needed — provider outage. Try again in a few minutes.",
          other: "Rotate or re-paste your key below if the issue persists.",
        },
      },
    },
    errors: {
      METHODOLOGY_RELINK_REASON_REQUIRED: "Enter the reason for changing the methodology version.",
      METHODOLOGY_VERSION_NOT_CHOOSABLE: "Only an approved or superseded methodology version can be chosen for an engagement.",
    },
    risk: { bin: { type: { sample_item_results_bulk: "Sample item results" } } },
  },
  ar: {
    s7e1: {
      help: {
        button: "كيف يعمل تبويب المنهجية",
        title: "كيف يعمل تبويب المنهجية",
        close: "إغلاق",
        sections: {
          what: {
            title: "ما هذا التبويب",
            paras: [
              "يحفظ هذا التبويب دليل المراجعة الخاص بمكتبكم (منهجيتكم) داخل AZEX.",
              "خطوات المراجعة في AZEX تتبع معايير المراجعة الدولية (ISAs) أصلاً. ويسجّل دليلكم الاختيارات التي يتخذها مكتبكم ضمنها — مثل نسبة الأهمية النسبية الإجمالية التي تستخدمونها للأهمية النسبية للأداء. ولا يحل الدليل محل معايير المراجعة الدولية.",
            ],
          },
          steps: {
            title: "الخطوات الأربع",
            numbered: true,
            items: [
              "ارفعوا دليلكم بصيغة PDF («إضافة منهجية»).",
              "يسرد AZEX كل قسم من الدليل مع صفحته — مثلاً «4.5.2 · الأهمية النسبية للأداء · 75 % من الأهمية النسبية الإجمالية · ص 18».",
              "يكتب AZEX ملخصاً قصيراً تحت 8 عناوين: الأهمية النسبية، والعينات، وتقييم المخاطر، ومعايير اختبار قيود اليومية، والإجراءات التحليلية، والمصادقات الخارجية، ومستويات المراجعة والتوقيع، والتوثيق والتجميع والأرشفة. والعنوان الذي لا يغطيه دليلكم يظهر عليه «غير مشمول في هذه المنهجية».",
              "يعتمده مدير، فيصبح الإصدار الحالي (V1، V2 …). ويمكن أيضاً رفض المسودة مع ذكر السبب.",
            ],
            paras: [
              "قائمة الأقسام والملخص مسودتان من الذكاء الاصطناعي: راجعوهما مقابل ملف PDF قبل الاعتماد.",
            ],
          },
          versions: {
            title: "الإصدارات",
            paras: [
              "تُختم كل مهمة جديدة بالإصدار الحالي يوم إنشائها، وتحتفظ بذلك الإصدار.",
              "مثال: اعتُمد V1 في يناير، فالمهام التي أُنشئت في فبراير تستخدم V1. ثم اعتمدتم V2 في يونيو: المهام التي تُنشأ بعد ذلك تستخدم V2. وتبقى مهام فبراير على V1 ما لم يغيّرها مدير مع ذكر السبب (شارة الرأس ← «تغيير إصدار المنهجية»).",
              "كل مهمة جديدة — تُنشأ من البداية أو بالترحيل إلى السنة التالية أو بالنسخ — تُختم بالإصدار الحالي وقت إنشائها. وتحتفظ المهمة القائمة بإصدارها إلى أن يغيّره مدير مع ذكر السبب.",
              "تُظهر شارة الرأس «المنهجية Vn» إصدار المهمة المفتوحة.",
              "عند اعتماد إصدار جديد يصبح الإصدار السابق «مُستبدل». وتبقى الإصدارات المستبدلة في القائمة مع ملف PDF الخاص بها.",
              "يُرفض «تغيير إصدار المنهجية» على مهمة موقّعة (مقفلة): افتح قفلها أولاً.",
              "لا يمكن حذف إصدار ما دام هو الإصدار المعتمد الحالي أو ما دامت أي مهمة تستخدمه (وتذكر الرسالة عدد المهام التي تستخدمه).",
              "عندما يغيّر مدير إصدار المهمة، تسري إعدادات المكتب للإصدار الجديد من الحفظ التالي. وتبقى الانحرافات المسجلة مع الإصدار الذي سُجّلت بموجبه؛ ويُغلق الانحراف عندما تعود قيمته إلى داخل النطاق الذي سُجّل مقابله.",
            ],
          },
          // STEP 7e-2 · S4: إعدادات المكتب، وأين تُستخدم، والانحرافات، وتقرير المكتب
          settings: {
            title: "إعدادات المكتب (مجموعة لكل إصدار)",
            items: [
              "لكل إصدار إعدادات المكتب الخاصة به (زر «إعدادات المكتب» في صفه): نطاق نسبة الأهمية النسبية (الحد الأدنى–الأعلى) لكل أساس — الربح قبل الضريبة، والإيرادات، وإجمالي الأصول، وحقوق الملكية، والمصروفات، وأساس آخر؛ ونسبة الأهمية النسبية للأداء ونسبة الحد الضئيل بوضوح من الأهمية النسبية الإجمالية (الحد الأدنى–الأعلى)؛ والحد الأدنى لمستوى الثقة في العينات %؛ والتحريف المتوقع الافتراضي % من التحريف المقبول؛ ومعايير المخاطر الجوهرية ومستويات المراجعة التي يطلبها مكتبكم (وهذان إرشاد للفريق فقط — لا يغيّران قواعد التوقيع أو القفل).",
              "«اقتراح من المنهجية» يقرأ القواعد والملخص اللذين سردهما AZEX ويقترح قيمة لكل إعداد مع القسم والصفحة — مثلاً «الأهمية النسبية للأداء % من الأهمية النسبية الإجمالية · 60–75 % · §4.5.2 · ص 18». تُقرأ القيمة من نص القاعدة؛ وللإعداد الذي لا يذكره نص القاعدة يُسأل الذكاء الاصطناعي ويجب أن يشير إلى أحد الأسطر المسرودة. والإعداد الذي لا يذكره دليلكم يظهر عليه «غير موجود في هذه المنهجية».",
              "يؤكد مدير كل إعداد قبل استخدامه — كما اقتُرح، أو بعد تعديله (وتبقى القيمة المقترحة بجانب التعديل). والإعداد غير المؤكد لا يُستخدم في أي مكان.",
              "عندما يُستبدل إصدار تُجمَّد إعداداته: تبقى قابلة للقراءة كسجل وتظل سارية على المهام التي تستخدم ذلك الإصدار.",
            ],
          },
          worksheets: {
            title: "أين تُستخدم الإعدادات",
            items: [
              "الأهمية النسبية (105(أ)): يظهر نطاق المكتب بجانب نسبة الأساس ونسبة الأهمية النسبية للأداء ونسبة الحد الضئيل بوضوح. ولا يملأ AZEX هذه الحقول عنكم. حفظ نسبة خارج النطاق يطلب سبباً؛ وبدونه يُرفض الحفظ. مثال: نطاق المكتب للأهمية النسبية للأداء 60–75 %؛ تُدخلون 80 % ← يطلب AZEX السبب ← مع السبب يُحفظ الرقم ويُسجَّل انحراف.",
              "العينات: يبدأ مستوى الثقة عند الحد الأدنى للمكتب؛ والمستوى الأقل منه يحتاج إلى سبب ويسجّل انحرافاً. ويبدأ التحريف المتوقع عند النسبة الافتراضية للمكتب من التحريف المقبول؛ ويمكنكم تغييره.",
              "الإعدادات المستخدمة هي إعدادات الإصدار الذي تستخدمه المهمة. والمهمة التي ليس لها إصدار خاص بها، أو الإصدار الذي ليست له إعدادات مؤكدة، أو المكتب الذي ليست له منهجية معتمدة، تعمل كما كانت دون أي فحوصات.",
            ],
          },
          deviations: {
            title: "الانحرافات",
            items: [
              "كل رقم يُحفظ خارج إعداد للمكتب هو انحراف واحد: المهمة، والمجال، والإعداد، وقيمة المكتب أو نطاقه، وقيمة المهمة، والسبب، ومن حفظه ومتى، وإصدار المنهجية. حفظ الحقل نفسه مرة أخرى يحدّث ذلك الانحراف؛ وإعادة القيمة إلى داخل النطاق تُغلقه.",
              "الانحراف الجديد يرسل إشعاراً إلى شريك المهمة، وإلى مراجع جودة الارتباط عند تعيينه.",
              "لوحة «انحرافات المنهجية» في صفحة تجميع الملف تعرض انحرافات المهمة للفريق والمراجع ومراجع جودة الارتباط. ولا يُقرّ بكل انحراف إلا شريك المهمة، مع ملاحظة اختيارية؛ ويُسجَّل الإقرار في سجل التدقيق. والقيمة أو السبب الجديد على انحراف مفتوح يحتاج إلى الإقرار مجدداً.",
              "ما دام انحراف مفتوح غير مُقرّ به، يُرفض توقيع الشريك وقفل الملف بهذه الرسالة (مثال بانحراف واحد):",
              "«رُفض توقيع الشريك وقفل الملف: لم يُقرّ شريك المهمة بعد بـ 1 انحراف/انحرافات عن المنهجية: الأهمية النسبية: الأهمية النسبية للأداء % 80 % (نطاق المكتب 60–75 %). يُقرّ شريك المهمة بكل انحراف في «انحرافات المنهجية» في صفحة تجميع الملف.»",
            ],
          },
          report: {
            title: "تقرير انحرافات المكتب",
            paras: [
              "في هذه الصفحة يرى المديرون كل انحراف في مهام المكتب مع حالته (مفتوح — غير مُقرّ به، مفتوح — مُقرّ به، مُغلق). صفّوا حسب المهمة والمجال والحالة، وصدّروا القائمة إلى Excel ‏(XLSX).",
            ],
          },
          assistant: {
            title: "أين يُستخدم أيضاً",
            paras: [
              "يجيب مساعد الذكاء الاصطناعي من قواعد دليلكم ويذكر القسم والصفحة — مثلاً [Methodology V1 §4.5.2, p.18]. وهو يقرأ إصدار المهمة المفتوحة، وليس مجرد أحدث إصدار.",
            ],
          },
          teamJob: {
            title: "ما يبقى من عمل الفريق",
            items: [
              "إجراءات المكتب التي ليس لها ورقة عمل في AZEX تُوثَّق في نموذجكم الخاص أو كمرفق.",
              "الموضوعات خارج العناوين الثمانية (مثل الاستمرارية، ومراجعة المجموعات، والأطراف ذات العلاقة، والتقديرات المحاسبية) يقتبسها المساعد من دليلكم؛ ولا يحولها AZEX إلى خطوات أو فحوصات.",
              "لا يحكم AZEX على مدى التزام دليلكم بمعايير المراجعة الدولية — فذلك حكم مهني لمكتبكم.",
            ],
          },
          who: {
            title: "من يمكنه فعل ماذا",
            items: [
              "رفع إصدار أو اعتماده أو رفضه أو حذفه، وإعادة تشغيل الاستخراج («إعادة المحاولة») أو ملخص الذكاء الاصطناعي، وتشغيل «اقتراح من المنهجية»، وتأكيد إعدادات المكتب لإصدار أو تغييرها، وتغيير إصدار مهمة، وفتح تقرير انحرافات المكتب: مسؤول الشركة، أو الشريك (بما في ذلك الشريك بلقب المدير التنفيذي)، أو المدير الأول، أو مدير المراجعة، أو المدير.",
              "مساعد المدير ليس منهم. ويمكن لكل من سواهم في المكتب فتح هذا التبويب وقراءة قائمة الأقسام والملخص وإعدادات المكتب وعرض ملف PDF أو تنزيله.",
              "الإقرار بالانحراف: شريك المهمة فقط.",
              "يُسجَّل في سجل التدقيق: رفع الإصدارات واعتمادها ورفضها وحذفها، وإعادة تشغيل الاستخراج أو ملخص الذكاء الاصطناعي، وتشغيل الاقتراحات، وتأكيد إعداد أو مسحه، وتغيير إصدار المهمة، وكل انحراف (تسجيله وتحديثه وإغلاقه) وكل إقرار.",
            ],
          },
        },
      },
      page: {
        wording: "إجراءات المراجعة المدمجة في AZEX تتبع معايير المراجعة الدولية (ISAs). وتضيف منهجية مكتبكم اختيارات المكتب الخاصة ضمن هذه المعايير؛ ولا تحل محلها.",
      },
      change: {
        menuOpen: "فتح صفحة المنهجية",
        menuChange: "تغيير إصدار المنهجية…",
        title: "تغيير إصدار المنهجية",
        uses: "تستخدم هذه المهمة: ⁦V{n}⁩",
        usesFallback: "ليس لهذه المهمة إصدار خاص بها؛ فهي تستخدم الإصدار المعتمد الحالي ⁦V{n}⁩.",
        pick: "الإصدار الجديد",
        pickPlaceholder: "اختر إصداراً",
        only: "لا يمكن اختيار إلا الإصدارات المعتمدة أو المستبدلة.",
        none: "لا يوجد إصدار معتمد أو مُستبدل للاختيار.",
        current: "(هذه المهمة)",
        reason: "السبب (مطلوب)",
        reasonPh: "لماذا تنتقل هذه المهمة إلى إصدار آخر",
        reasonRequired: "أدخل سبب تغيير إصدار المنهجية.",
        audit: "يُسجَّل التغيير في سجل التدقيق مع الإصدار القديم والجديد وسببك.",
        save: "تغيير الإصدار",
        cancel: "إلغاء",
        done: "تم تغيير إصدار المنهجية: ⁦V{old} → V{new}⁩",
      },
      tenant: {
        editTitle: "تعديل المستأجر",
        firmName: "اسم المكتب",
        plan: "الخطة",
        pickPlan: "اختر خطة",
        removePlan: "— إزالة الخطة (يحتفظ المكتب بحدوده الحالية) —",
        planItem: "{name} · {users} مستخدم · {engs} مهمة",
        planHint: "اختيار خطة ينسخ حدود المستخدمين والمهام الخاصة بها (الخطة التي لا حد فيها للمهام = مهام غير محدودة). إزالة الخطة تُبقي حدود المكتب الحالية. ولا يُحذف أي مستخدم أو مهمة قائمة إذا تجاوزت الحد.",
        cancel: "إلغاء",
        save: "حفظ",
      },
      aiu: {
        headRuntime: "فشل مفتاحكم الخاص في استدعاء حديث للذكاء الاصطناعي — وخُدم الطلب بمفتاح المنصة بدلاً منه.",
        headSetup: "استخدم آخر استدعاء مفتاح المنصة، وليس مفتاحكم الخاص.",
        reasonLbl: "السبب:",
        whenLbl: "متى:",
        reason: {
          no_key_stored: "لا يوجد مفتاح محفوظ",
          decryption_failed: "تعذّر فك التشفير",
          runtime_401: "رفض المزوّد المفتاح (401)",
          runtime_403: "منع المزوّد الطلب (403)",
          runtime_429: "قيّد المزوّد معدل الطلبات (429)",
          runtime_5xx: "خطأ في خادم المزوّد",
          model_retired: "أوقف المزوّد النموذج المحفوظ (404)",
          runtime_error: "خطأ لدى المزوّد",
          unknown: "غير معروف",
        },
        fix: {
          decryption_failed: "الحل: أعيدوا لصق مفتاحكم أدناه — النص المشفّر المحفوظ غير قابل للقراءة.",
          no_key_stored: "الحل: الصقوا مفتاح المزوّد لتفعيل التوجيه عبر مفتاحكم الخاص.",
          runtime_401: "الحل: يفيد المزوّد بأن المفتاح غير صالح أو أُلغي — الصقوا مفتاحاً سارياً.",
          runtime_403: "الحل: تم التحقق من المفتاح لكنه يفتقر إلى الصلاحية — راجعوا لوحة تحكم المزوّد.",
          runtime_429: "لا يلزم أي إجراء — تقييد المعدل مؤقت، وستعود حصة المزوّد.",
          runtime_5xx: "لا يلزم أي إجراء — انقطاع لدى المزوّد. حاولوا مجدداً بعد بضع دقائق.",
          other: "بدّلوا مفتاحكم أو أعيدوا لصقه أدناه إذا استمرت المشكلة.",
        },
      },
    },
    errors: {
      METHODOLOGY_RELINK_REASON_REQUIRED: "أدخل سبب تغيير إصدار المنهجية.",
      METHODOLOGY_VERSION_NOT_CHOOSABLE: "لا يمكن اختيار إلا إصدار معتمد أو مُستبدل من المنهجية للمهمة.",
    },
    risk: { bin: { type: { sample_item_results_bulk: "نتائج بنود العينة" } } },
  },
};

export default I18N_S7E1;
